import {createCellRenderer} from "../../helpers";
import createElement from "../../../createElement";
import {CheckboxHeaderComponent} from "./Components/CheckboxHeaderComponent";
import {randomUniqueId} from "../../../inputs/random/strings";

/**
 * @param {import('../../grids').GridInstance} gridInstance During initialization this contains the
 * grid options component, afterward it will contain the grid instance
 * @param columnDef
 */
function setupRowSelectionColumn(gridInstance, columnDef) {
	// Save custom fields
	const idField = columnDef._rowPickerIdField;
	const pickerName = columnDef._rowPickerName;

	// Remove custom properties so AG grid doesn't complain about it later
	delete columnDef._customType;
	delete columnDef._rowPickerIdField;
	delete columnDef._rowPickerName;

	// Register custom checkbox header component
	if (!gridInstance.components) {
		gridInstance.components = {};
	}
	gridInstance.components.customHeaderCheckboxComponent = CheckboxHeaderComponent;


	// Setup colId
	columnDef.colId = pickerName;
	// Setup cell renderer
	columnDef.cellRenderer = createCellRenderer(params => {
		const inputContainer = createElement(`
			<div class="input-container d-flex w-100 h-100 justify-content-center align-items-center">
				<input
					class="form-check cursor-pointer"
					type="checkbox"
					autocomplete="off"
					${isRowSelected(params) ? 'checked' : ''}
				>
			</div>
		`)

		const checkboxElement = inputContainer.querySelector('input');

		// Setup event handler
		checkboxElement.addEventListener('change', () => {
			handleCheckboxChange(params, checkboxElement);
		})

		// Redirect click on container to checkbox element
		inputContainer.addEventListener('click', (e) => {
			// Toggle inner checkbox element, unless we clicked on it directly
			if (e.target === checkboxElement) {
				return;
			}

			checkboxElement.checked = !checkboxElement.checked;
			checkboxElement.dispatchEvent(new InputEvent('change'));
		})

		return inputContainer;
	})
	// Assign custom checkbox header to col definition
	columnDef.headerComponent = 'customHeaderCheckboxComponent';

	// Pass gridOptions/gridInstance to header by using the window object.
	// We can't pass it using the component params because the original
	// instance is lost somewhere in the process.
	const tempId = randomUniqueId('ROW_SELECTION_RANDOM_TEMP_ID_FOR_HEADER');
	columnDef.headerComponentParams = {
		gridInstanceGlobalTempId: tempId,
		pickerName: pickerName,
	}

	// If not cleaned up, this may be a cause of memory leak no?
	// noinspection JSValidateTypes
	window[tempId] = gridInstance;


	/** Methods */

	/**
	 * @return {GridCustomColumnRowSelectionDefs.CustomRowSelectionState}
	 */
	function getRowSelectionState() {
		// Initializes state for custom row selection columns
		if (!gridInstance._meta.customRowSelectionState) {
			gridInstance._meta.customRowSelectionState = {}
		}

		// Initializes state for this custom row selection column
		if (!gridInstance._meta.customRowSelectionState[pickerName]) {
			gridInstance._meta.customRowSelectionState[pickerName] = getCustomSelectionInitialState()
		}

		return gridInstance._meta.customRowSelectionState[pickerName];
	}

	/**
	 * @return {CheckboxHeaderComponent}
	 */
	function getRowSelectionHeader() {
		return gridInstance._meta.customRowSelectionStateHeader[pickerName];
	}

	/**
	 * @param row
	 * @param {HTMLInputElement} checkbox
	 */
	function handleCheckboxChange(row, checkbox) {
		// noinspection JSUnresolvedReference
		const selectionState = getRowSelectionState();

		const state = getRowSelectionState();
		switch (state.selectionType) {
			case "simple":
				// If simple selection, check if it was selected
				if (checkbox.checked) {
					selectionState.selected[getRowDataPickerId(row)] = true;
				} else {
					delete selectionState.selected[getRowDataPickerId(row)];
				}
				break;
			case "all":
				if (checkbox.checked) {
					delete selectionState.excluded[getRowDataPickerId(row)];
				} else {
					selectionState.excluded[getRowDataPickerId(row)] = true;
				}
				break;
		}

		// Let header know a change occurred
		getRowSelectionHeader().handleRowSelection();
	}

	/**
	 * @param row
	 * @return {*}
	 */
	function getRowDataPickerId(row) {
		return row.data[idField]
	}

	/**
	 * @param row
	 * @return {boolean}
	 */
	function isRowSelected(row) {
		const state = getRowSelectionState();

		switch (state.selectionType) {
			case "simple":
				// If simple selection, check if it was selected
				return !!state.selected[getRowDataPickerId(row)];
			case "all":
				return !state.excluded[getRowDataPickerId(row)];
		}
	}
}

function getCustomSelectionInitialState() {
	return {
		selectionType: 'simple',
		selected: {},
		excluded: {},
	};
}

export {setupRowSelectionColumn, getCustomSelectionInitialState}
